import { type InputHTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ui from '../styles/ui.module.css';
import styles from './PasswordInput.module.css';

/** An eye; struck through when the password is shown (pressing it hides the password again). */
function EyeIcon({ struck }: { struck: boolean }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
      <circle cx={12} cy={12} r={3} />
      {struck && <path d="M4 20 20 4" />}
    </svg>
  );
}

/**
 * A password field with an eye button that shows what was typed and hides it again. Give the field
 * its label with `<label htmlFor={id}>` outside this component, so the button's name does not
 * become part of the field's name.
 */
export function PasswordInput({ id, className, ...input }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { id: string }) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  return (
    <div className={styles.wrap}>
      <input {...input} id={id} type={shown ? 'text' : 'password'} className={`${ui.input} ${styles.input} ${className ?? ''}`} />
      <button
        type="button"
        className={styles.toggle}
        aria-controls={id}
        aria-pressed={shown}
        aria-label={shown ? t('common.hidePassword') : t('common.showPassword')}
        title={shown ? t('common.hidePassword') : t('common.showPassword')}
        onClick={() => setShown((s) => !s)}
      >
        <EyeIcon struck={shown} />
      </button>
    </div>
  );
}
